// Email-mode "Sections" — pre-built, 600px-optimized, email-safe content blocks (the beginner gold:
// drop a finished section and edit it in place). See DND_EMAIL_MODE_SPEC.md + the placement review.
//
// Each section is ONE `email-row` (data-gjs-type="email-row"), so it inherits the beginner protocol:
// column-locked drop, reorder via toolbar/drag, and a LOCKED inner scaffold. Content is edit-in-place
// (double-click text, click image) but NOT draggable (ND) — a beginner only ever moves whole sections.
// Markup is table-based with inline styles + explicit widths (email-safe), sized for a 600px body;
// multi-column cells carry class="email-col" and stack on mobile.
//
// Self-contained (its own helpers) so it can't destabilise the basic rows in ./blocks.js.

import { getSocialImageUrl } from '../Social/utils';

const IMG = 'https://cdn11.waymore.io/dnd/images/image_placeholder.jpg';
const FONT = 'Arial, Helvetica, sans-serif';
const BRAND = '#d983a6';
const BRAND_DARK = '#b4638a';

// LOCKED structural table/tr/td (can't be selected/edited/dropped-into).
const LOCK =
  'data-gjs-selectable="false" data-gjs-hoverable="false" data-gjs-editable="false" data-gjs-droppable="false"';
// Content: edit-in-place but not draggable.
const ND = 'data-gjs-draggable="false"';

// Mobile stacking for side-by-side cells (extracted into CSS on drop; survives save).
const STACK =
  '<style>@media (max-width:480px){.email-col{display:block !important;width:100% !important}.email-col-pad{padding:16px 0 0 !important}.email-gutter{display:none !important}}</style>';

// One full-width Email row wrapping section markup, centred on a 600px body. `bg` = row background;
// `pad` = inner padding.
const section = (inner, bg = '#ffffff', pad = '24px') => `
  <table data-gjs-type="email-row" data-type="section" role="presentation" width="100%" style="width:100%;border-collapse:collapse;background-color:${bg};">
    <tbody ${LOCK}>
      <tr ${LOCK}>
        <td ${LOCK} align="center" style="padding:0;">
          <table role="presentation" width="600" ${LOCK} style="width:600px;max-width:600px;border-collapse:collapse;">
            <tbody ${LOCK}><tr ${LOCK}><td ${LOCK} style="padding:${pad};">${inner}</td></tr></tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table>`;

const heading = (text, size = 22, color = '#1a1a1a') =>
  `<h1 ${ND} style="margin:0 0 10px;font-family:${FONT};font-size:${size}px;line-height:1.3;color:${color};font-weight:bold;">${text}</h1>`;

const paragraph = (text, color = '#555555', extra = '') =>
  `<p ${ND} style="margin:0 0 16px;font-family:${FONT};font-size:15px;line-height:1.6;color:${color};${extra}">${text}</p>`;

// Outlook-bulletproof CTA: padding + background on a <td bgcolor> (Outlook honours these; it ignores
// them on an inline <a>), with the <a> carrying only the label + colour. The wrapper table/td are
// LOCKED so a beginner edits only the label; the <a> stays the editable `button` component.
const button = (label, bg = BRAND, color = '#ffffff') => `
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" ${LOCK} style="margin:0 auto;border-collapse:collapse;"><tbody ${LOCK}><tr ${LOCK}>
    <td ${LOCK} align="center" bgcolor="${bg}" style="border-radius:6px;padding:13px 28px;text-align:center;">
      <a href="#" ${ND} data-gjs-type="button" data-type="button" class="bpreview-button" style="display:inline-block;color:${color};text-decoration:none;font-family:${FONT};font-size:16px;font-weight:bold;line-height:1;">${label}</a>
    </td>
  </tr></tbody></table>`;

const link = (label, color = BRAND_DARK) =>
  `<a href="#" ${ND} style="font-family:${FONT};font-size:15px;color:${color};text-decoration:none;font-weight:bold;">${label}</a>`;

const image = (w = '100%', radius = '6px') =>
  `<img ${ND} data-gjs-type="image" data-type="image" src="${IMG}" alt="image" style="display:block;width:${w};max-width:100%;height:auto;border:0;border-radius:${radius};" />`;

// Two side-by-side cells with an 8% gutter; stacks on mobile.
const twoCol = (left, right, lw = '46%', rw = '46%', valign = 'top') => `
  <table role="presentation" width="100%" ${LOCK} style="width:100%;border-collapse:collapse;"><tbody ${LOCK}><tr ${LOCK}>
    <td ${LOCK} class="email-col" width="${lw}" valign="${valign}" style="width:${lw};vertical-align:${valign};">${left}</td>
    <td ${LOCK} class="email-gutter" width="8%" style="width:8%;">&nbsp;</td>
    <td ${LOCK} class="email-col" width="${rw}" valign="${valign}" style="width:${rw};vertical-align:${valign};">${right}</td>
  </tr></tbody></table>${STACK}`;

// One equal cell of a 3-column row.
const thirdCol = (inner) =>
  `<td ${LOCK} class="email-col" width="31%" valign="top" style="width:31%;vertical-align:top;">${inner}</td>`;

// A social icon bar (PNG icons; email-safe).
const socialBar = (baseUrl) =>
  `<div ${ND} style="text-align:center;">` +
  ['facebook', 'twitter', 'instagram', 'linkedin']
    .map(
      (n) =>
        `<a href="#" ${ND} style="display:inline-block;margin:0 5px;"><img ${ND} src="${getSocialImageUrl(
          { baseUrl, network: n },
        )}" alt="${n}" width="28" height="28" style="display:inline-block;border:0;" /></a>`,
    )
    .join('') +
  `</div>`;

// A checklist-style feature item (tick + text row).
const featureItem = (title, text) => `
  <table role="presentation" width="100%" ${LOCK} style="width:100%;border-collapse:collapse;margin-bottom:12px;"><tbody ${LOCK}><tr ${LOCK}>
    <td ${LOCK} width="36" valign="top" style="width:36px;vertical-align:top;font-family:${FONT};font-size:20px;color:${BRAND};line-height:1.3;">&#10003;</td>
    <td ${LOCK} valign="top" style="vertical-align:top;">${heading(
  title,
  16,
)}${paragraph(text, '#666666', 'margin:0;')}</td>
  </tr></tbody></table>`;

// SVG palette icons (editor UI only; not email content).
const ICON = {
  header:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 4h20v5H2zm2 2v1h6V6zm10 0v1h6V6zM2 11h20v9H2zm2 2v5h16v-5z"/></svg>',
  hero: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 4h16v10H4zM2 2v14h20V2zm2 18h6v2H4zm8 0h8v2h-8z"/></svg>',
  cta: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 7h20v10H2zM6 10h8v4H6zm10 0h4v1h-4zm0 3h4v1h-4z"/></svg>',
  feature:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 5h8v14H3zm10 2h8v2h-8zm0 4h8v2h-8zm0 4h6v2h-6z"/></svg>',
  article:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 4h7v7H4zm9 0h7v2h-7zm0 4h7v2h-7zm-9 5h16v2H4zm0 4h16v2H4z"/></svg>',
  product:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 4h16v9H4zM2 2v13h20V2zm4 15h5v2H6zm0 3h8v1H6zm10-3h2v5h-2z"/></svg>',
  cols3:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 5h6v14H2zm7 0h6v14H9zm7 0h6v14h-6z"/></svg>',
  cols2:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 5h8v14H3zm10 0h8v14h-8z"/></svg>',
  imageText:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M13 5h8v14h-8zM3 7h8v2H3zm0 4h8v2H3zm0 4h6v2H3z"/></svg>',
  quote:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M7 7h5v6H9v-2h1V9H7zm7 0h5v6h-3v-2h1V9h-3z"/></svg>',
  list: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 5h2v2H4zm4 0h12v2H8zM4 11h2v2H4zm4 0h12v2H8zM4 17h2v2H4zm4 0h12v2H8z"/></svg>',
  footer:
    '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 15h20v5H2zm3 2v1h6v-1zm10 0v1h4v-1zM4 4h16v2H4zm0 4h12v2H4z"/></svg>',
};

// The section catalogue (top-to-bottom email flow order). `baseUrl` powers the social icons.
export const buildEmailSections = ({ baseUrl } = {}) => [
  {
    id: 'email-sec-header',
    label: 'Header',
    media: ICON.header,
    content: section(
      twoCol(
        `<img ${ND} data-gjs-type="image" data-type="image" src="${IMG}" alt="logo" style="display:block;width:130px;max-width:100%;height:auto;border:0;" />`,
        `<p ${ND} style="margin:0;font-family:${FONT};font-size:13px;line-height:1.5;color:#555555;text-align:right;">Home&nbsp;&nbsp;|&nbsp;&nbsp;Shop&nbsp;&nbsp;|&nbsp;&nbsp;About</p>`,
        '50%',
        '46%',
        'middle',
      ),
    ),
  },
  {
    id: 'email-sec-hero',
    label: 'Hero',
    media: ICON.hero,
    content: section(
      `<div style="text-align:center;">${image(
        '100%',
      )}<div style="height:16px;"></div>${heading(
        'Your big headline',
        26,
      )}${paragraph(
        'A short line that tells readers what this email is about and why it matters.',
      )}${button('Get started')}</div>`,
    ),
  },
  {
    id: 'email-sec-feature',
    label: 'Feature',
    media: ICON.feature,
    content: section(
      twoCol(
        image('100%'),
        `${heading('Feature title', 20)}${paragraph(
          'Describe the feature or product in a sentence or two, then invite a click.',
        )}${button('Learn more')}`,
      ),
    ),
  },
  {
    id: 'email-sec-article',
    label: 'Article',
    media: ICON.article,
    content: section(
      twoCol(
        image('100%'),
        `${heading('Article title', 18)}${paragraph(
          'A short teaser of the article — one or two lines to make the reader curious.',
        )}${link('Read more &rarr;')}`,
        '38%',
        '54%',
      ),
    ),
  },
  {
    id: 'email-sec-product',
    label: 'Product',
    media: ICON.product,
    content: section(
      twoCol(
        image('100%'),
        `${heading(
          'Product name',
          20,
        )}<p ${ND} style="margin:0 0 12px;font-family:${FONT};font-size:18px;color:${BRAND_DARK};font-weight:bold;">$29.00</p>${paragraph(
          'One line about the product.',
        )}${button('Shop now')}`,
      ),
    ),
  },
  {
    id: 'email-sec-imagetext',
    label: 'Text + Image',
    media: ICON.imageText,
    content: section(
      twoCol(
        `${heading('Title', 20)}${paragraph(
          'Text on the left, image on the right — a nice alternating rhythm down the email.',
        )}${button('Learn more')}`,
        image('100%'),
        '54%',
        '38%',
      ),
    ),
  },
  {
    id: 'email-sec-3col',
    label: '3 Columns',
    media: ICON.cols3,
    content: section(
      `<table role="presentation" width="100%" ${LOCK} style="width:100%;border-collapse:collapse;"><tbody ${LOCK}><tr ${LOCK}>${thirdCol(
        `${image('100%')}<div style="height:8px;"></div>${heading(
          'One',
          16,
        )}${paragraph('Short supporting text.', '#666666', 'margin:0;')}`,
      )}<td ${LOCK} width="3%" style="width:3%;">&nbsp;</td>${thirdCol(
        `${image('100%')}<div style="height:8px;"></div>${heading(
          'Two',
          16,
        )}${paragraph('Short supporting text.', '#666666', 'margin:0;')}`,
      )}<td ${LOCK} width="3%" style="width:3%;">&nbsp;</td>${thirdCol(
        `${image('100%')}<div style="height:8px;"></div>${heading(
          'Three',
          16,
        )}${paragraph('Short supporting text.', '#666666', 'margin:0;')}`,
      )}</tr></tbody></table>${STACK}`,
    ),
  },
  {
    id: 'email-sec-2col',
    label: '2 Columns',
    media: ICON.cols2,
    content: section(
      twoCol(
        `${heading('Column one', 18)}${paragraph(
          'Text for the first column goes here.',
        )}`,
        `${heading('Column two', 18)}${paragraph(
          'Text for the second column goes here.',
        )}`,
      ),
    ),
  },
  {
    id: 'email-sec-testimonial',
    label: 'Testimonial',
    media: ICON.quote,
    content: section(
      `<p ${ND} style="margin:0 0 14px;font-family:${FONT};font-size:19px;line-height:1.5;color:#333333;text-align:center;font-style:italic;">&ldquo;A short, glowing quote from a happy customer goes right here.&rdquo;</p><div ${ND} style="text-align:center;"><img ${ND} data-gjs-type="image" data-type="image" src="${IMG}" alt="avatar" style="display:inline-block;width:48px;height:48px;border:0;border-radius:50%;" /><p ${ND} style="margin:8px 0 0;font-family:${FONT};font-size:14px;color:#1a1a1a;font-weight:bold;">Jane Doe</p><p ${ND} style="margin:0;font-family:${FONT};font-size:12px;color:#888888;">Marketing Lead</p></div>`,
      '#f7f7f7',
    ),
  },
  {
    id: 'email-sec-list',
    label: 'Feature list',
    media: ICON.list,
    content: section(
      `${featureItem(
        'First benefit',
        'A short description of this benefit.',
      )}${featureItem(
        'Second benefit',
        'A short description of this benefit.',
      )}${featureItem(
        'Third benefit',
        'A short description of this benefit.',
      )}`,
    ),
  },
  {
    id: 'email-sec-cta',
    label: 'Call to action',
    media: ICON.cta,
    content: section(
      `<div style="text-align:center;">${heading(
        'Ready to get started?',
        22,
        '#ffffff',
      )}${paragraph(
        'Join thousands who already did — it only takes a minute.',
        '#f3e6ee',
      )}${button('Sign up now', '#ffffff', BRAND_DARK)}</div>`,
      BRAND,
    ),
  },
  {
    // Footer ships an unsubscribe link by construction (deliverability).
    id: 'email-sec-footer',
    label: 'Footer',
    media: ICON.footer,
    content: section(
      `${socialBar(
        baseUrl,
      )}<div style="height:12px;"></div><p ${ND} style="margin:0;font-family:${FONT};font-size:12px;line-height:1.6;color:#888888;text-align:center;">Company Name &middot; 123 Street, City, Country<br/>You received this email because you subscribed to our list.<br/><a href="{$unsubscribeLink}" style="color:#888888;text-decoration:underline;">Unsubscribe</a></p>`,
      '#f7f7f7',
    ),
  },
];

// Register the sections as BlockManager blocks under the given category (default "Sections").
export default function (editor, { category = 'Sections', baseUrl } = {}) {
  const bm = editor.BlockManager;
  buildEmailSections({ baseUrl }).forEach(({ id, label, media, content }) =>
    bm.add(id, {
      label,
      category,
      media,
      select: true,
      activate: true,
      content,
    }),
  );
}
